import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { listMyShares, listMyShareViews, revokeMyShare } from "../repo/itineraryMyShareRepo";
import type { ItineraryMyShare } from "../types/itineraryMyShare";

/** 管理本人分享的查询、查看明细与撤销刷新，按员工隔离缓存。 */
export function useItineraryMyShares(ownerId: number | undefined, enabled: boolean) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ page: 1, pageSize: 20, keyword: "" });
  const [selectedShare, setSelectedShare] = useState<ItineraryMyShare>();
  const [viewPage, setViewPage] = useState({ page: 1, pageSize: 20 });
  const rootKey = ["itinerary", "my-shares", ownerId] as const;
  const query = useQuery({
    queryKey: [...rootKey, "list", filters],
    queryFn: () => listMyShares(filters),
    enabled: enabled && ownerId !== undefined,
    retry: false,
  });
  const views = useQuery({
    queryKey: [...rootKey, "views", selectedShare?.id, viewPage],
    queryFn: () => listMyShareViews(selectedShare!.id, viewPage.page, viewPage.pageSize),
    enabled: enabled && ownerId !== undefined && Boolean(selectedShare),
    retry: false,
  });
  const revoke = useMutation({
    mutationFn: revokeMyShare,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rootKey }),
  });

  return {
    query,
    views,
    filters,
    viewPage,
    selectedShare,
    revoke,
    search: (keyword: string) => setFilters((current) => ({ ...current, keyword: keyword.trim(), page: 1 })),
    changePage: (page: number, pageSize: number) => setFilters((current) => ({
      ...current, page: pageSize !== current.pageSize ? 1 : page, pageSize,
    })),
    showViews: (share: ItineraryMyShare) => {
      setSelectedShare(share);
      setViewPage({ page: 1, pageSize: 20 });
    },
    closeViews: () => setSelectedShare(undefined),
    changeViewPage: (page: number, pageSize: number) => setViewPage((current) => ({
      page: pageSize !== current.pageSize ? 1 : page, pageSize,
    })),
  };
}
